// Hosts a live view element (camera <video> or demo <canvas>) with a
// pixel-accurate overlay canvas redrawn every animation frame.

import { useEffect, useRef, type ReactNode } from 'react';
import type { Vec2 } from '../../core/geometry';
import { containRect, type Rect } from '../draw/overlay';

export interface StageProps {
  /** Element to mount (owned by the frame source). */
  view?: HTMLVideoElement | HTMLCanvasElement | null;
  /** Source pixel size (for letterboxing). */
  size: () => { w: number; h: number };
  draw?: (ctx: CanvasRenderingContext2D, rect: Rect, dpr: number) => void;
  onTap?: (p: Vec2, e: PointerEvent) => void;
  onDrag?: (p: Vec2, phase: 'start' | 'move' | 'end') => void;
  className?: string;
  children?: ReactNode;
}

export function Stage({ view, size, draw, onTap, onDrag, className, children }: StageProps) {
  const host = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawRef = useRef(draw);
  const sizeRef = useRef(size);
  drawRef.current = draw;
  sizeRef.current = size;

  useEffect(() => {
    const el = host.current;
    if (!el || !view) return;
    view.classList.add('view');
    el.prepend(view);
    if (view instanceof HTMLVideoElement) void view.play().catch(() => {});
    return () => {
      if (view.parentElement === el) el.removeChild(view);
    };
  }, [view]);

  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const c = canvas.current;
      const el = host.current;
      if (c && el) {
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        const w = Math.round(el.clientWidth * dpr);
        const h = Math.round(el.clientHeight * dpr);
        if (c.width !== w || c.height !== h) {
          c.width = w;
          c.height = h;
        }
        const ctx = c.getContext('2d')!;
        ctx.clearRect(0, 0, w, h);
        const s = sizeRef.current();
        if (drawRef.current && s.w) drawRef.current(ctx, containRect(w, h, s.w, s.h), dpr);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const el = host.current;
    if (!el || (!onTap && !onDrag)) return;
    const toNorm = (e: PointerEvent): Vec2 | null => {
      const b = el.getBoundingClientRect();
      const s = sizeRef.current();
      const r = containRect(b.width, b.height, s.w, s.h);
      const x = (e.clientX - b.left - r.x) / r.w;
      const y = (e.clientY - b.top - r.y) / r.h;
      return x >= -0.02 && x <= 1.02 && y >= -0.02 && y <= 1.02 ? { x, y } : null;
    };
    let dragging = false;
    const down = (e: PointerEvent) => {
      const p = toNorm(e);
      if (!p) return;
      dragging = true;
      el.setPointerCapture(e.pointerId);
      onDrag?.(p, 'start');
    };
    const move = (e: PointerEvent) => {
      if (!dragging) return;
      const p = toNorm(e);
      if (p) onDrag?.(p, 'move');
    };
    const up = (e: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      const p = toNorm(e);
      if (p) {
        onDrag?.(p, 'end');
        onTap?.(p, e);
      }
    };
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    return () => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
    };
  }, [onTap, onDrag]);

  return (
    <div ref={host} className={`stage ${className ?? ''}`}>
      <canvas ref={canvas} className="overlay" />
      {children}
    </div>
  );
}
